import { useState, useCallback, useContext } from "react";
import { Button, Text, TextField, Flex, Dialog } from "@radix-ui/themes";
import { toast } from "sonner";
import { useCreateWorkspace } from "@/api/workspace/Workspace.api";
import { CreateWorkspaceRequest } from "@/api/workspace/Workspace.model";
import { FeatureAccessContext } from "@/context/featureAccess";

interface CreateWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkspaceCreated?: () => void;
}

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onWorkspaceCreated,
}: CreateWorkspaceDialogProps) {
  const [workspaceName, setWorkspaceName] = useState("");
  const [error, setError] = useState("");
  const { setSelectedWorkspace } = useContext(FeatureAccessContext);

  const { mutateAsync: createWorkspace, isLoading } = useCreateWorkspace();

  const validateForm = useCallback(() => {
    if (!workspaceName.trim()) {
      setError("Workspace name is required");
      return false;
    }

    if (!/^[a-z0-9-]+$/i.test(workspaceName)) {
      setError("Workspace name can only contain letters, numbers, and hyphens");
      return false;
    }

    setError("");
    return true;
  }, [workspaceName]);

  const handleNameChange = useCallback(
    (value: string) => {
      setWorkspaceName(value);
      // Clear error when user starts typing
      if (error) {
        setError("");
      }
    },
    [error]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const requestData: CreateWorkspaceRequest = {
        email: `${workspaceName}@quack.ai`,
        subdomain: workspaceName,
        createDefaultSampleConfiguration: false,
      };

      await createWorkspace(requestData);

      // Notify parent component
      onWorkspaceCreated?.();

      toast.success(`Workspace "${workspaceName}" created successfully!`, {
        action: {
          label: "Switch to Workspace",
          onClick: () => {
            setSelectedWorkspace(workspaceName);
          },
        },
      });

      // Reset form
      setWorkspaceName("");
      setError("");

      // Close dialog
      onOpenChange(false);
    } catch (error: any) {
      console.error("failed to create workspace:", error);
      toast.error(
        error?.message || "Failed to create workspace. Please try again."
      );
    }
  }, [
    workspaceName,
    validateForm,
    createWorkspace,
    onOpenChange,
    onWorkspaceCreated,
    setSelectedWorkspace,
  ]);

  const handleCancel = useCallback(() => {
    setWorkspaceName("");
    setError("");
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Create New Workspace</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Enter a name for your new workspace.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Workspace Name *
            </Text>
            <TextField.Root
              placeholder="Enter workspace name (e.g., my-company)"
              value={workspaceName}
              onChange={(e) => handleNameChange(e.target.value.toLowerCase())}
              disabled={isLoading}
            />
            {error && (
              <Text size="1" color="red" mt="1">
                {error}
              </Text>
            )}
          </label>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!workspaceName.trim()}
            highContrast
          >
            Create Workspace
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
